import { useCallback, useEffect, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useReference } from "lib/@hooks/use-reference"
import { useDialog } from "lib/@hooks/useDialog"
import { useCurrentUser } from "lib/store/use-current-user"
import { useDemand } from "lib/authorization/use-demand"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { confirm } from "lib/@dialogs/confirm"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { getSharingLink } from "routes/sharing/controller/get-sharing-link"
import { getTreeId } from "routes/schedule/lib/getTreeId"
import { getScheduleRecordId } from "library/get-unique-id-from-tree.mjs"
import {
    SHARE_LINK_COMMENT_SHARE_PARAM,
    SHARE_LINK_COMMENT_TARGET_REFERENCE,
    buildScheduleCommentAnchor,
    buildScheduleCommentSelectionMetadata,
    filterShareLinkCommentThreads,
    shouldShowShareLinkCommentsTab,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import {
    createShareLinkScheduleComment,
    deleteShareLinkScheduleComment,
    editShareLinkScheduleComment,
    getRegimeScheduleComments,
    getShareLinkCommentContext,
    getShareLinkRecipientUnread,
    getShareLinkRegimeUnread,
    getShareLinkScheduleComments,
    markShareLinkCommentThreadsRead,
    markShareLinkRegimeCommentThreadsRead,
    replyToShareLinkScheduleComment,
    resolveShareLinkScheduleComment,
} from "routes/schedule/plugins/share-link-comments/share-link-comments.controller"
import {
    commentExistsInThreads,
    getCreatedCommentId,
    getDayGroups,
    getVisibleThreads,
} from "./share-link-comment-feed-helpers"
import { ShareLinkCommentComposer } from "./share-link-comment-composer"
import { ShareLinkCommentToolbar } from "./share-link-comment-toolbar"
import { ShareLinkCommentDayGroup } from "./share-link-comment-day-group"
import { ShareLinkCommentEditDialog } from "./share-link-comment-edit-dialog"
import { isPublishedRegimeId, isUnqualifiedRegimeId } from "./share-link-comment-regime-id"
import { useShareLinkCommentReadTracker } from "./use-share-link-comment-read-tracker"
import { isEnabled } from "lib/@components/feature"
import { FEATURE_FLAG_SHARE_LINK_COMMENTS } from "./share-link-comments-feature-flag"

const EMPTY_COMMENTS = []
const EMPTY_UNREAD = { total: 0, unreadThreadIds: [] }

export function ShareLinkCommentsPanel() {
    const { code, currentId, schedule, schedulePath, isShare } = useBoundContext()
    const [shareLinkId] = useSearchParam("share", null)
    const [commentShareLinkId] = useSearchParam(SHARE_LINK_COMMENT_SHARE_PARAM, null)
    const [currentVersion] = useSearchParam("version", null)
    const [statusFilter] = useSearchParam("commentFilter", "open")
    const [targetFilter] = useSearchParam("commentTarget", "all")
    const [selectedCommentId, setComment] = useSearchParam("comment", null)
    const routeCommentsLinkId = shareLinkId ?? commentShareLinkId
    const selectedCommentContext = getShareLinkCommentContext.useResults(
        { commentId: getSelectedCommentContextId() },
        withInitialValue(IS_LOADING)
    )
    const recoveredCommentContext = getRecoveredCommentContext()
    const commentsLinkId = routeCommentsLinkId ?? recoveredCommentContext?.shareLinkId
    const [rightBarReference, setRightBarReference] = useReference(
        { [SHARE_LINK_COMMENT_TARGET_REFERENCE]: null },
        "tab-ScheduleRightBar-"
    )
    const activeSchedulePath = schedulePath ?? currentId
    const commentTarget = getCurrentCommentTarget()
    const shareLink = getSharingLink.useResults(commentsLinkId)
    const anchor = buildScheduleCommentAnchor({ schedule })
    const treeId = getTreeId(activeSchedulePath)
    // Comments live on the PUBLISHED regime version the share link was created from. When this
    // schedule is open via the editable DRAFT tree (unqualified tree id), keep reading that published
    // version so a reviewer who switched to the draft keeps the thread context while editing.
    const isDraftTreeView = !isPublishedRegimeId(treeId) && isUnqualifiedRegimeId(treeId)
    const regimeId = getCommentRegimeId()
    const scheduleId = getCommentScheduleId()
    const currentUser = useCurrentUser()
    const isShareLinkUser = useDemand("$sharing")
    const isRegimeEditor = useDemand("manageRegime")
    // Internal reviewers see EVERY share link's comments for this schedule via the regime-scoped query —
    // no share-link URL parameter required. Recipients keep the link-scoped query (they always arrive
    // through their own link). "Recipient" is the reliable signal here: recipients hold the `$sharing`
    // demand, so anyone WITHOUT it is internal. (Gating on `manageRegime` was too narrow — a regime
    // reviewer without that exact client demand fell through to the link path and saw nothing.) The
    // server's `canEdit` gate authorises the regime query, so a non-editor simply gets an empty list.
    const isInternal = isRegimeEditor || !isShareLinkUser
    const internalResult = getRegimeScheduleComments.useResults(
        {
            regimeId: isInternal ? regimeId : null,
            scheduleId: isInternal ? scheduleId : null,
            schedulePath: isInternal ? getCommentsSchedulePath() : null,
        },
        withInitialValue(IS_LOADING)
    )
    const recipientResult = getShareLinkScheduleComments.useResults(
        { shareLinkId: isInternal ? null : commentsLinkId, regimeId, scheduleId, schedulePath: activeSchedulePath },
        withInitialValue(IS_LOADING)
    )
    const rawResult = isInternal ? internalResult : recipientResult
    const internalUnread =
        getShareLinkRegimeUnread.useResults({ regimeId: isInternal ? regimeId : null }) ?? EMPTY_UNREAD
    const recipientUnread =
        getShareLinkRecipientUnread.useResults({
            shareLinkId: isInternal ? null : commentsLinkId,
            regimeId: isInternal ? null : regimeId,
        }) ?? EMPTY_UNREAD
    const unread = isInternal ? internalUnread : recipientUnread
    const { unreadThreadSet, markThreadSeen } = useShareLinkCommentReadTracker({
        unreadThreadIds: unread.unreadThreadIds,
        mark: markReadThreads,
    })
    const [pendingPublishId, setPendingPublishId] = useState(null)
    const openEditDialog = useDialog(<ShareLinkCommentEditDialog />)
    const finishPublish = useCallback(finishPublishCallback, [setRightBarReference])
    const viewer = {
        isShareLinkUser: isShareLinkUser && !isRegimeEditor,
        userId: currentUser.id,
    }
    // Authoring a NEW thread still needs a comment-enabled share link to attach to; replies/resolves use
    // each comment's own link. So internal reviewers without a link can view and reply, but the "new
    // comment" composer and selection targets only appear when a comment-enabled link is in context.
    // The feature demand is also required (recipients inherit it from the link owner's client auth
    // bundle), so when the feature/auth bundle is disabled the recipient surface closes rather than
    // bleeding through the link's persisted allowComments (FIQ-1668). The server re-checks every write.
    const canCreate =
        shouldShowShareLinkCommentsTab({
            isShare: !!isShare || !!shareLinkId,
            shareLinkId: commentsLinkId,
            shareLink,
        }) && isEnabled(FEATURE_FLAG_SHARE_LINK_COMMENTS)
    const canShow = isInternal ? !!regimeId && !!scheduleId : canCreate

    useEffect(clearPublishWhenCommentAppears, [rawResult, pendingPublishId, finishPublish])

    if (isRecoveringSelectedCommentContext()) return <LoadingScreen />
    if (!canShow) return null
    if (rawResult === IS_LOADING) return <LoadingScreen />

    const rawComments = rawResult ?? EMPTY_COMMENTS
    const comments = filterShareLinkCommentThreads(rawComments, viewer)
    const visibleThreads = getVisibleThreads(comments, { statusFilter, targetFilter })
    const dayGroups = getDayGroups(visibleThreads)
    const commentPresentationSchedule = getCommentPresentationSchedule()

    return (
        <Stack spacing={1} data-cy="share-link-comments-panel" sx={{ flex: 1, minHeight: 0, overflow: "visible" }}>
            <ShareLinkCommentToolbar comments={comments} />
            <Box
                data-cy="share-link-comments-feed"
                sx={{ flex: 1, minHeight: 0, overflow: "visible", display: "flex", flexDirection: "column" }}
            >
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <Bound
                        schedule={commentPresentationSchedule}
                        commentList={rawComments}
                        viewer={viewer}
                        markThreadSeen={markThreadSeen}
                        unreadThreadSet={unreadThreadSet}
                        onReply={replyToComment}
                        onResolve={resolveComment}
                        onEdit={editComment}
                        onDelete={deleteComment}
                        onJump={jumpToComment}
                    >
                        <Repeat
                            list={dayGroups}
                            pass="group"
                            keyFn={getGroupKey}
                            item={<ShareLinkCommentDayGroup />}
                            fallback={
                                <Typography variant="body2" color="text.secondary">
                                    No comments
                                </Typography>
                            }
                        />
                    </Bound>
                </Box>
                {getNewCommentComposer()}
            </Box>
        </Stack>
    )

    function getGroupKey(group) {
        return group.key
    }

    async function createComment(body) {
        try {
            const created = await createShareLinkScheduleComment({
                shareLinkId: commentsLinkId,
                regimeId,
                scheduleId,
                schedulePath: activeSchedulePath,
                scheduleTitle: schedule?.title,
                scheduleVersion: getDisplayedScheduleVersion(),
                content: body,
                selectionMetadata: getSelectionMetadata(),
            })
            const createdId = getCreatedCommentId(created)
            if (createdId) setPendingPublishId(createdId)
            else finishPublish()
        } catch (e) {
            setPendingPublishId(null)
            errorSnackbar("Comments are not available yet")
        }
    }

    function getNewCommentComposer() {
        if (!canCreate) return null
        return (
            <Box
                data-cy="share-link-comment-new-sticky"
                sx={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                    mt: "auto",
                    pt: 1,
                    pb: 0.5,
                    bgcolor: "background.paper",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    boxShadow: (theme) => theme.customShadows?.z8 ?? "0 -8px 16px rgba(145, 158, 171, 0.12)",
                }}
            >
                <ShareLinkCommentComposer
                    dataCy="share-link-comment-new"
                    label={getComposerLabel()}
                    buttonLabel="Comment"
                    autoFocus={!!commentTarget}
                    publishing={!!pendingPublishId}
                    onSubmit={createComment}
                    contextLabel={commentTarget?.targetLabel}
                    contextText={commentTarget?.targetText}
                    onClearContext={clearCommentTarget}
                />
            </Box>
        )
    }

    async function replyToComment(comment, body) {
        try {
            return await replyToShareLinkScheduleComment({
                shareLinkId: comment.shareLinkId ?? commentsLinkId,
                regimeId: getCommentActionRegimeId(comment),
                scheduleId,
                threadId: comment.threadId ?? comment.id,
                content: body,
            })
        } catch (e) {
            errorSnackbar("Comments are not available yet")
            return null
        }
    }

    async function resolveComment(comment, resolved) {
        try {
            await resolveShareLinkScheduleComment({
                shareLinkId: comment.shareLinkId ?? commentsLinkId,
                regimeId: getCommentActionRegimeId(comment),
                scheduleId,
                threadId: comment.threadId ?? comment.id,
                resolved,
            })
        } catch (e) {
            errorSnackbar("Couldn't update the comment")
        }
    }

    async function deleteComment(message) {
        try {
            const confirmed = await confirm("Delete this comment?")
            if (!confirmed) return
            await deleteShareLinkScheduleComment({
                shareLinkId: message.shareLinkId ?? commentsLinkId,
                regimeId: getCommentActionRegimeId(message),
                scheduleId,
                commentId: message.id,
            })
        } catch (e) {
            errorSnackbar("Couldn't delete the comment")
        }
    }

    async function editComment(message) {
        try {
            const newText = await openEditDialog({ initialBody: message.body ?? "" })
            if (!newText) return
            await editShareLinkScheduleComment({
                shareLinkId: message.shareLinkId ?? commentsLinkId,
                regimeId: getCommentActionRegimeId(message),
                scheduleId,
                commentId: message.id,
                content: newText,
            })
        } catch (e) {
            errorSnackbar("Couldn't save your changes")
        }
    }

    function jumpToComment(comment) {
        setComment(comment.id)
    }

    function markReadThreads({ threadIds }) {
        if (isInternal) {
            if (!regimeId) return undefined
            return markShareLinkRegimeCommentThreadsRead({ regimeId, threadIds })
        }
        if (!commentsLinkId || !regimeId) return undefined
        return markShareLinkCommentThreadsRead({ shareLinkId: commentsLinkId, regimeId, threadIds })
    }

    function getCommentScheduleId() {
        if (recoveredCommentContext?.scheduleId) return recoveredCommentContext.scheduleId
        if (code) return code
        if (activeSchedulePath) return getScheduleRecordId(activeSchedulePath)
        return schedule?.id
    }

    function getCommentRegimeId() {
        if (recoveredCommentContext?.regimeId) return recoveredCommentContext.regimeId
        if (isDraftTreeView && shareLink?.regime) return shareLink.regime
        if (isDraftTreeView) return `live|${treeId}`
        return treeId
    }

    function getCommentActionRegimeId(comment) {
        return comment?.regimeId ?? regimeId
    }

    // Stored comment paths reference the version/location where the thread was created. When opening a
    // selected thread from the regime comments directory, use the schedule id only so a moved schedule
    // or alternate tree route cannot filter out the very thread being opened.
    function getCommentsSchedulePath() {
        if (selectedCommentId) return null
        if (isDraftTreeView) return null
        return activeSchedulePath
    }

    function getSelectionMetadata() {
        if (commentTarget?.selectionMetadata) return commentTarget.selectionMetadata
        return buildScheduleCommentSelectionMetadata({ anchor, schedule, scheduleId })
    }

    function getDisplayedScheduleVersion() {
        const urlVersion = parseScheduleVersion(currentVersion)
        if (urlVersion !== undefined) return urlVersion
        return parseScheduleVersion(schedule?.version)
    }

    function getCommentPresentationSchedule() {
        const displayedVersion = getDisplayedScheduleVersion()
        if (displayedVersion === undefined) return schedule
        if (!schedule) return { version: displayedVersion }
        return {
            ...schedule,
            version: displayedVersion,
        }
    }

    function parseScheduleVersion(version) {
        if (version === undefined || version === null || version === "") return undefined
        const parsed = Number.parseInt(version, 10)
        if (Number.isNaN(parsed)) return undefined
        return parsed
    }

    function getCurrentCommentTarget() {
        const target = rightBarReference[SHARE_LINK_COMMENT_TARGET_REFERENCE]
        if (!target) return null
        if (target.schedulePath && target.schedulePath !== activeSchedulePath) return null
        return target
    }

    function clearCommentTarget() {
        setRightBarReference({ [SHARE_LINK_COMMENT_TARGET_REFERENCE]: null })
    }

    function getComposerLabel() {
        if (commentTarget?.targetLabel) return `Comment on ${commentTarget.targetLabel}`
        return "Leave a schedule comment"
    }

    function clearPublishWhenCommentAppears() {
        if (!pendingPublishId || rawResult === IS_LOADING) return
        const threads = rawResult ?? EMPTY_COMMENTS
        if (!commentExistsInThreads(threads, pendingPublishId)) return
        finishPublish()
    }

    function getSelectedCommentContextId() {
        if (routeCommentsLinkId) return null
        return selectedCommentId
    }

    function getRecoveredCommentContext() {
        if (selectedCommentContext === IS_LOADING) return null
        return selectedCommentContext
    }

    function isRecoveringSelectedCommentContext() {
        return !routeCommentsLinkId && !!selectedCommentId && selectedCommentContext === IS_LOADING
    }

    function finishPublishCallback() {
        setPendingPublishId(null)
        setRightBarReference({ [SHARE_LINK_COMMENT_TARGET_REFERENCE]: null })
    }
}
